import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { MeProvider, useMe } from './state.tsx';
import { Home } from './Home.tsx';
import { Login } from './Login.tsx';
import { QuestMap, GameCard, Play, PayReturn } from '../quest/Quest.tsx';
import { Profile, MergeAsk, type LinkResult } from '../profile/Profile.tsx';
import { Toasts, Screen, toast } from './ui.tsx';
import { api } from '../platform/api.ts';
import { t, errText } from '../i18n/index.ts';

const Admin = lazy(() => import('../admin/Admin.tsx'));

// возврат с VK на сайт (?code=…&state=…&device_id=…): вход или привязка — сервер знает по state
function VkReturn({ onDone }: { onDone: () => void }) {
  const { reload } = useMe();
  const nav = useNavigate();
  const [ask, setAsk] = useState<LinkResult | null>(null);
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    history.replaceState(null, '', location.pathname);
    api('auth_vk', { code: q.get('code'), device_id: q.get('device_id') ?? '', state: q.get('state') })
      .then(async r => { if (r.need_confirm) setAsk(r); else { await reload(); onDone(); nav(r.linked ? '/profile' : '/', { replace: true }); } })
      .catch(e => { toast(errText(e)); onDone(); nav('/', { replace: true }); });
  }, [reload, nav, onDone]);
  return (
    <Screen title={t('login.vkReturning')}>
      <p><span className="spin" /></p>
      {ask && <MergeAsk ask={ask} onDone={async () => { await reload(); onDone(); nav('/profile', { replace: true }); }} />}
    </Screen>
  );
}

function Routed() {
  const { ready, me } = useMe();
  const [vk, setVk] = useState(() => {
    const q = new URLSearchParams(location.search);
    return !!q.get('code') && /^[0-9a-f]{32}$/.test(q.get('state') ?? '');
  });
  const vkDone = useCallback(() => setVk(false), []);
  if (vk) return <VkReturn onDone={vkDone} />;
  if (!ready) return <main className="screen center"><span className="spin" /></main>;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/q/:slug" element={<QuestMap />} />
      <Route path="/g/:slug" element={<GameCard />} />
      <Route path="/g/:slug/:part" element={<Play />} />
      <Route path="/pay" element={<PayReturn />} />
      <Route path="/profile" element={<Profile key={me?.profile.id ?? 'none'} />} />
      <Route path="/admin" element={me?.role ? <Suspense fallback={<span className="spin" />}><Admin /></Suspense> : <Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <MeProvider>
        <Routed />
        <Toasts />
      </MeProvider>
    </BrowserRouter>
  );
}
